import React from "react";
import { Controller } from "react-hook-form";
import type {
  Control,
  DeepMap,
  FieldError,
  FormState,
  FormStateProxy,
} from "react-hook-form";
import styled from "styled-components";
import { TextField } from "../TextFields/TextFields";
import { useTranslation } from "react-i18next";
import { spacingTokens } from "../../designTokens";

const HorizontalContainer = styled.div`
  max-width: 500px;
  display: grid;
  grid-template-columns: 48% auto 48%;
  flex-wrap: nowrap;
  overflow: hidden;
  div.text_errorMessageWrapper {
    position: unset;
  }
`;

const VerticalContainer = styled.div`
  display: flex;
  flex-direction: column;
  div.text_errorMessageWrapper {
    position: unset;
  }
`;

const GreyLineWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const GreyLine = styled.div<{ $vertical?: boolean }>`
  height: ${({ $vertical }) => ($vertical ? "15px" : "1px")};
  width: ${({ $vertical }) => ($vertical ? "1px" : "100%")};
  margin-bottom: ${({ $vertical }) => ($vertical ? "0" : undefined)};
  border: solid ${({ theme }) => theme.resolve("border_md")}
    ${({ theme }) => theme.primaryBorder};
`;

export type LinkAttributeValue = { url: string; display_text: string };

type Props = {
  /** The react-hook-form field name (attribute/column id) */
  name: string;
  /** Label prefix used for both sub-fields, e.g. "Website" → "Website - Display Text" / "Website - URL" */
  label: string;
  displayTextLabel?: string;
  urlLabel?: string;
  control: Control;
  register: (...args: any[]) => any;
  errors: DeepMap<Record<string, any>, FieldError>;
  formState: FormStateProxy<Record<string, any>>;
  defaultValue?: LinkAttributeValue;
  /** Whether the whole field is required. display_text becomes required when url is filled OR when field is required. */
  isRequired?: boolean;
  isDisabled?: boolean;
  /** Layout of the two text fields. Defaults to "horizontal". */
  layout?: "horizontal" | "vertical";
  updateAssetName?: (name: string) => void; // Optional callback to update asset name in parent form when display text changes
};

/**
 * A controlled react-hook-form field for link attributes.
 * Renders two TextFields (Display Text + URL) separated by a divider.
 */
export function LinkAttributeField({
  name,
  label,
  displayTextLabel,
  urlLabel,
  control,
  register,
  errors,
  formState,
  defaultValue = { url: "", display_text: "" },
  isRequired = false,
  isDisabled = false,
  layout = "horizontal",
  updateAssetName,
}: Props) {
  const { t } = useTranslation();

  const isVertical = layout === "vertical";
  const Container = isVertical ? VerticalContainer : HorizontalContainer;

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ onChange, value: formValue }) => {
        const displayTextIsRequired = !!(formValue?.url || isRequired);

        return (
          <Container>
            <TextField
              label={displayTextLabel ?? `${label} - ${t("Display Text")}`}
              name={"display_text"}
              type={"text"}
              theref={register({ required: displayTextIsRequired })}
              formState={formState as unknown as FormState<Record<string, any>>}
              errors={errors}
              error={(errors[name] as any)?.["display_text"]}
              defaultValue={defaultValue.display_text}
              value={formValue?.display_text ?? ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const textValue = event.target.value;
                onChange({
                  ...formValue,
                  display_text: textValue,
                });
                updateAssetName?.(textValue);
              }}
              readOnly={isDisabled}
            />
            <GreyLineWrapper
              style={
                isVertical
                  ? {
                      justifyContent: "center",
                      marginBottom: spacingTokens.spacing_none,
                    }
                  : undefined
              }
            >
              <GreyLine $vertical={isVertical} />
            </GreyLineWrapper>
            <TextField
              name={"url"}
              type={"url"}
              label={urlLabel ?? `${label} - URL`}
              formState={formState as unknown as FormState<Record<string, any>>}
              errors={errors}
              error={(errors[name] as any)?.["url"]}
              defaultValue={defaultValue.url}
              value={formValue?.url ?? ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onChange({ ...formValue, url: event.target.value })
              }
              theref={register({ required: false })}
              readOnly={isDisabled}
            />
          </Container>
        );
      }}
    />
  );
}
